import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPassword, verifyResetCode, resetPassword } from '../services/forgotPassword.service';

/* ── Step indicator ── */
const StepIndicator = ({ current, total }) => (
  <div className="fp-steps">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`fp-step-dot ${i + 1 === current ? 'active' : ''} ${i + 1 < current ? 'done' : ''}`}
      >
        {i + 1 < current ? <i className="fas fa-check" /> : i + 1}
      </div>
    ))}
  </div>
);

/* ── Step 1: Saisie email ── */
const EmailStep = ({ email, setEmail, loading, error, onSubmit }) => {
  const [localError, setLocalError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setLocalError('Veuillez entrer votre adresse email.');
      return;
    }
    setLocalError(null);
    onSubmit();
  };

  return (
    <div className="fp-step-content">
      <div className="fp-icon-wrap">
        <i className="fas fa-lock" />
      </div>
      <h2 className="fp-title">Mot de passe oublié ?</h2>
      <p className="fp-subtitle">
        Saisissez votre adresse email. Vous recevrez un code de réinitialisation à 6 chiffres.
      </p>

      {(error || localError) && (
        <div className="fp-error">
          <i className="fas fa-circle-exclamation" />
          <span>{error || localError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="fp-form-group">
          <label className="fp-label">Adresse email</label>
          <div className="fp-input-wrap">
            <i className="fas fa-envelope fp-input-icon" />
            <input
              type="email"
              className="fp-input"
              placeholder="artiste@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
          </div>
        </div>

        <button type="submit" className="fp-btn" disabled={loading}>
          {loading ? (
            <><i className="fas fa-circle-notch fa-spin" /> Envoi en cours...</>
          ) : (
            <><i className="fas fa-paper-plane" /> Envoyer le code</>
          )}
        </button>
      </form>
    </div>
  );
};

/* ── Step 2: Vérification du code ── */
const CodeStep = ({ email, onCodeVerified, onBack }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [manualCode, setManualCode] = useState('');
  const [useManualInput, setUseManualInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSubmittedCode, setLastSubmittedCode] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [canResend, setCanResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [codeSentAt, setCodeSentAt] = useState(() => Date.now());
  const inputRefs = useRef([]);
  const loadingRef = useRef(false);
  const manualInputRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - codeSentAt) / 1000);
      const remaining = Math.max(0, 15 * 60 - elapsed);
      setTimeLeft(remaining);
      setCanResend(remaining === 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [codeSentAt]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const handleSubmit = useCallback(async (codeValue) => {
    if (loadingRef.current) return;
    setLoading(true);
    setError(null);

    try {
      const data = await verifyResetCode(email, codeValue);
      onCodeVerified(data.reset_token);
    } catch (err) {
      setError(err.message);
      setCode(['', '', '', '', '', '']);
      setManualCode('');
      setLastSubmittedCode(null);
      if (!useManualInput) {
        inputRefs.current[0]?.focus();
      } else {
        manualInputRef.current?.focus();
      }
    } finally {
      setLoading(false);
    }
  }, [email, onCodeVerified, useManualInput]);

  const handleManualSubmit = useCallback(() => {
    const codeValue = useManualInput ? manualCode.replace(/\D/g, '') : code.join('');
    if (codeValue.length === 6) {
      setLastSubmittedCode(codeValue);
      handleSubmit(codeValue);
    }
  }, [manualCode, code, useManualInput, handleSubmit]);

  // Auto-submit on complete
  useEffect(() => {
    const codeString = code.join('');
    const isComplete = codeString.length === 6 && code.every(d => d !== '');
    if (isComplete && !loading && codeString !== lastSubmittedCode) {
      setLastSubmittedCode(codeString);
      const timeoutId = setTimeout(() => handleSubmit(codeString), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [code, loading, handleSubmit, lastSubmittedCode]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(0, 1);
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedData) return;
    const newCode = [...code];
    for (let i = 0; i < 6; i++) newCode[i] = pastedData[i] || '';
    setCode(newCode);
    if (pastedData.length === 6) {
      inputRefs.current[5]?.focus();
      inputRefs.current[5]?.select();
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError(null);
    try {
      await forgotPassword(email);
      setCodeSentAt(Date.now());
      setTimeLeft(15 * 60);
      setCanResend(false);
      setCode(['', '', '', '', '', '']);
      setManualCode('');
      setLastSubmittedCode(null);
      if (!useManualInput) inputRefs.current[0]?.focus();
      else manualInputRef.current?.focus();
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  const toggleInputMode = () => {
    setUseManualInput(!useManualInput);
    setError(null);
    if (!useManualInput) {
      setManualCode(code.join(''));
      setTimeout(() => manualInputRef.current?.focus(), 100);
    } else {
      const digits = manualCode.split('').slice(0, 6);
      const newCode = ['', '', '', '', '', ''];
      digits.forEach((d, i) => { if (i < 6) newCode[i] = d; });
      setCode(newCode);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  return (
    <div className="fp-step-content">
      <div className="fp-icon-wrap">
        <i className="fas fa-envelope" />
      </div>
      <h2 className="fp-title">Vérifiez votre email</h2>
      <p className="fp-subtitle">
        Un code de vérification a été envoyé à
      </p>
      <p className="fp-email-highlight">{email}</p>

      {error && (
        <div className="fp-error">
          <i className="fas fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {!useManualInput ? (
        <div className="fp-code-inputs">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={loading}
              className="fp-code-cell"
            />
          ))}
        </div>
      ) : (
        <div className="fp-code-single-wrap">
          <input
            ref={manualInputRef}
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="Entrez le code à 6 chiffres"
            value={manualCode}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setManualCode(val);
            }}
            disabled={loading}
            className="fp-code-single"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && manualCode.length === 6) handleManualSubmit();
            }}
          />
        </div>
      )}

      <button
        onClick={handleManualSubmit}
        disabled={loading || (useManualInput ? manualCode.length !== 6 : code.join('').length !== 6)}
        className="fp-btn"
      >
        {loading ? (
          <><i className="fas fa-circle-notch fa-spin" /> Vérification...</>
        ) : (
          <><i className="fas fa-check-circle" /> Vérifier le code</>
        )}
      </button>

      <button onClick={toggleInputMode} disabled={loading} className="fp-link-btn">
        {useManualInput ? (
          <><i className="fas fa-th" /> Utiliser les cases séparées</>
        ) : (
          <><i className="fas fa-keyboard" /> Saisir dans un champ unique</>
        )}
      </button>

      <div className="fp-timer-section">
        {timeLeft > 0 ? (
          <p className="fp-timer-text">
            Code expire dans <span className="fp-timer-value">{formatTime(timeLeft)}</span>
          </p>
        ) : (
          <p className="fp-timer-expired">Code expiré</p>
        )}
      </div>

      <div className="fp-resend-section">
        {canResend || timeLeft === 0 ? (
          <button onClick={handleResend} disabled={resendLoading} className="fp-resend-btn">
            {resendLoading ? (
              <><i className="fas fa-circle-notch fa-spin" /> Envoi...</>
            ) : (
              <><i className="fas fa-redo" /> Renvoyer le code</>
            )}
          </button>
        ) : (
          <p className="fp-resend-timer">Renvoyer disponible dans {formatTime(timeLeft)}</p>
        )}
      </div>

      <button onClick={onBack} disabled={loading} className="fp-back-btn">
        <i className="fas fa-arrow-left" /> Retour
      </button>
    </div>
  );
};

/* ── Step 3: Nouveau mot de passe ── */
const NewPasswordStep = ({ resetToken, onBack }) => {
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const passwordStrength = (() => {
    const pwd = formData.new_password;
    if (!pwd) return { label: '', color: 'transparent', width: '0%' };
    if (pwd.length < 6) return { label: 'Faible', color: '#dc2626', width: '25%' };
    if (pwd.length < 8) return { label: 'Moyen', color: '#f59e0b', width: '50%' };
    if (pwd.length < 12) return { label: 'Bon', color: '#10b981', width: '75%' };
    return { label: 'Très bon', color: '#059669', width: '100%' };
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.new_password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (formData.new_password !== formData.confirm_password) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await resetPassword(resetToken, formData.new_password, formData.confirm_password);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fp-step-content">
        <div className="fp-icon-wrap fp-icon-success">
          <i className="fas fa-check" />
        </div>
        <h2 className="fp-title">Mot de passe réinitialisé !</h2>
        <p className="fp-subtitle">
          Votre mot de passe a été mis à jour avec succès.
        </p>
        <p className="fp-subtitle" style={{ marginTop: '0.5rem' }}>
          Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
        </p>
        <Link to="/login" className="fp-btn" style={{ textDecoration: 'none', display: 'inline-flex', marginTop: '1rem' }}>
          <i className="fas fa-arrow-right" /> Se connecter
        </Link>
      </div>
    );
  }

  return (
    <div className="fp-step-content">
      <div className="fp-icon-wrap">
        <i className="fas fa-key" />
      </div>
      <h2 className="fp-title">Nouveau mot de passe</h2>
      <p className="fp-subtitle">
        Choisissez un mot de passe sécurisé d'au moins 8 caractères.
      </p>

      {error && (
        <div className="fp-error">
          <i className="fas fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="fp-form-group">
          <label className="fp-label">Nouveau mot de passe</label>
          <div className="fp-input-wrap">
            <i className="fas fa-lock fp-input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="new_password"
              className="fp-input"
              placeholder="••••••••"
              value={formData.new_password}
              onChange={handleChange}
              required
              minLength={8}
              autoFocus
              disabled={loading}
            />
          </div>
          {formData.new_password && (
            <div className="fp-strength-bar-wrap">
              <div className="fp-strength-bar">
                <div
                  className="fp-strength-fill"
                  style={{ width: passwordStrength.width, background: passwordStrength.color }}
                />
              </div>
              <span className="fp-strength-label" style={{ color: passwordStrength.color }}>
                {passwordStrength.label}
              </span>
            </div>
          )}
        </div>

        <div className="fp-form-group">
          <label className="fp-label">Confirmer le mot de passe</label>
          <div className="fp-input-wrap">
            <i className="fas fa-lock fp-input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirm_password"
              className="fp-input"
              placeholder="••••••••"
              value={formData.confirm_password}
              onChange={handleChange}
              required
              minLength={8}
              disabled={loading}
            />
          </div>
          {formData.confirm_password && formData.new_password !== formData.confirm_password && (
            <p className="fp-mismatch">Les mots de passe ne correspondent pas</p>
          )}
        </div>

        <label className="fp-show-password">
          <input
            type="checkbox"
            checked={showPassword}
            onChange={() => setShowPassword(!showPassword)}
          />
          <span>Afficher les mots de passe</span>
        </label>

        <button type="submit" className="fp-btn" disabled={loading}>
          {loading ? (
            <><i className="fas fa-circle-notch fa-spin" /> Enregistrement...</>
          ) : (
            <><i className="fas fa-check" /> Réinitialiser le mot de passe</>
          )}
        </button>
      </form>

      <button onClick={onBack} disabled={loading} className="fp-back-btn">
        <i className="fas fa-arrow-left" /> Retour
      </button>
    </div>
  );
};

/* ── Main Page ── */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSendCode = async () => {
    setLoading(true);
    setError(null);
    try {
      await forgotPassword(email);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeVerified = (token) => {
    setResetToken(token);
    setStep(3);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
      setError(null);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="fp-page">
      <div className="fp-left">
        {/* Branding */}
        <Link to="/" className="fp-brand">
          <img src="/assets/Logo.png" alt="Pass-IO" style={{ height: '36px' }} />
        </Link>

        <div className="fp-form-container">
          <StepIndicator current={step} total={3} />
          {step === 1 && (
            <EmailStep
              email={email}
              setEmail={setEmail}
              loading={loading}
              error={error}
              onSubmit={handleSendCode}
            />
          )}
          {step === 2 && (
            <CodeStep
              email={email}
              onCodeVerified={handleCodeVerified}
              onBack={handleBack}
            />
          )}
          {step === 3 && (
            <NewPasswordStep
              resetToken={resetToken}
              onBack={handleBack}
            />
          )}

          {/* Footer link */}
          <div className="fp-footer">
            <Link to="/login" className="fp-footer-link">
              <i className="fas fa-arrow-left" /> Retour à la connexion
            </Link>
          </div>
        </div>
      </div>

      <div className="fp-right">
        <div className="fp-right-glow" />
        <div className="fp-right-content">
          <div className="fp-right-icon">
            <i className="fas fa-shield-halved" />
          </div>
          <h2 className="fp-right-title">Sécurité avant tout</h2>
          <p className="fp-right-text">
            La réinitialisation de mot de passe est protégée par un code à usage unique
            envoyé à votre adresse email. Ce code expire après 15 minutes.
          </p>
          <div className="fp-right-badges">
            <span className="fp-badge">Code à 6 chiffres</span>
            <span className="fp-badge">15 min d'expiration</span>
            <span className="fp-badge">Usage unique</span>
          </div>
        </div>
      </div>
    </div>
  );
}
